<script setup lang="ts">
import { computed, ref, watchEffect } from "vue";
import { useI18n } from "vue-i18n";

import { maskCPF, maskCNPJ, maskDateBR } from "@/common/utils/mask";

import {
  baseInput,
  baseDate,
  baseSelect,
  baseTextarea,
} from "@/common/components";

import type {
  EmployeeType,
  EmployeePayloadType,
} from "@/common/types/employee";

const { t } = useI18n();

const emit = defineEmits(["update-data"]);

const props = withDefaults(
  defineProps<{
    data: EmployeePayloadType | null;
    title?: string;
    width?: string;
    isPatch?: boolean;
  }>(),
  {
    width: "100%",
    isPatch: false,
  },
);

const defaultData = {
  name: "",
  social_name: null,
  role: null,
  sector: null,
  registration_number: null,
  document_type: "",
  document_name: "",
  document_number: "",
  status: "ACTIVE",
  ges_name: null,
  ges_status: null,
  ges_description: null,
  start_date: null,
  end_date: null,
  description: null,
};

const documentOptions = computed(() => [
  {
    option: t("common.labels.cpf"),
    value: "CPF",
  },
  {
    option: t("common.labels.cnpj"),
    value: "CNPJ",
  },
  {
    option: t("common.labels.other"),
    value: "OTHER",
  },
]);

const statusOptions = computed(() => [
  {
    option: t("common.labels.active"),
    value: "ACTIVE",
  },
  {
    option: t("common.labels.inactive"),
    value: "INACTIVE",
  },
]);

const payload = ref<EmployeePayloadType>({ ...defaultData });

function update(key: keyof typeof payload.value) {
  if (!props.isPatch) {
    emit("update-data", payload.value);
    return;
  }

  emit("update-data", { [key]: payload.value[key] });
}

function documentMask(value: any) {
  if (payload.value.document_type === "CPF") return maskCPF(value);

  if (payload.value.document_type === "CNPJ") return maskCNPJ(value);

  return value;
}

function changeDocument(type: any) {
  payload.value.document_name = type;
  payload.value.document_number = documentMask(payload.value.document_number);

  if (type === "OTHER") {
    payload.value.document_name = "";
  }

  if (!props.isPatch) {
    emit("update-data", payload.value);
    return;
  }

  emit("update-data", {
    document_name: payload.value.document_name,
    document_number: payload.value.document_number,
  });
}

watchEffect(() => {
  if (props.data) {
    payload.value = {
      ...defaultData,
      ...props.data,
    };
  }
});
</script>

<template>
  <section class="form-content" :style="{ width: width }">
    <form>
      <base-input
        v-model="payload.name"
        :value="payload.name"
        :placeholder="t('common.placeholders.name')"
        @input="update('name')"
      >
        <template #label> {{ t("common.labels.name") }} *</template>
      </base-input>

      <base-input
        v-model="payload.social_name"
        :value="payload.social_name"
        @input="update('social_name')"
      >
        <template #label> {{ t("common.labels.social_name") }}</template>
      </base-input>

      <div class="documents">
        <template v-if="payload.document_type === 'OTHER'">
          <div class="flex flex-row gap-4">
            <base-select
              class="flex-2"
              v-model="payload.document_type"
              :value="payload.document_type"
              :options="documentOptions"
              @change="changeDocument"
            >
              <template #label>
                {{ t("common.labels.document_type") }} *
              </template>
            </base-select>

            <base-input
              class="flex-3"
              v-model="payload.document_name"
              :value="payload.document_name"
              :placeholder="t('common.placeholders.describe')"
              @input="update('document_name')"
            >
              <template #label> {{ t("common.labels.name") }} *</template>
            </base-input>
          </div>

          <base-input
            class="flex-4"
            v-model="payload.document_number"
            :value="payload.document_number"
            :placeholder="t('common.labels.number')"
            :mask="documentMask"
            @input="update('document_number')"
          >
            <template #label> {{ t("common.labels.number") }} *</template>
          </base-input>
        </template>

        <template v-else>
          <div class="flex flex-row gap-1">
            <base-select
              class="flex-2"
              v-model="payload.document_type"
              :value="payload.document_type"
              :options="documentOptions"
              @change="changeDocument"
            >
              <template #label>
                {{ t("common.labels.document_type") }} *
              </template>
            </base-select>

            <base-input
              class="flex-3"
              v-model="payload.document_number"
              :value="payload.document_number"
              :placeholder="t('common.labels.number')"
              :mask="documentMask"
              @input="update('document_number')"
            >
              <template #label> {{ t("common.labels.number") }} *</template>
            </base-input>
          </div>
        </template>
      </div>

      <div class="flex flex-row gap-4">
        <base-select
          class="flex-1"
          v-model="payload.status"
          :value="payload.status"
          :options="statusOptions"
          @change="update('status')"
        >
          <template #label> {{ t("common.labels.status") }}</template>
        </base-select>

        <base-input
          class="flex-1"
          v-model="payload.registration_number"
          :value="payload.registration_number"
          @input="update('registration_number')"
        >
          <template #label>
            {{ t("employee.labels.registration_number") }}</template
          >
        </base-input>
      </div>

      <base-input
        v-model="payload.role"
        :value="payload.role"
        @input="update('role')"
      >
        <template #label> {{ t("employee.labels.role") }}</template>
      </base-input>

      <base-input
        v-model="payload.sector"
        :value="payload.sector"
        @input="update('sector')"
      >
        <template #label> {{ t("employee.labels.sector") }}</template>
      </base-input>

      <base-input
        v-model="payload.ges_name"
        :value="payload.ges_name"
        @input="update('ges_name')"
      >
        <template #label> {{ t("employee.labels.ges_name") }}</template>
      </base-input>

      <base-select
        v-model="payload.ges_status"
        :value="payload.ges_status"
        :options="statusOptions"
        @change="update('ges_status')"
      >
        <template #label> {{ t("employee.labels.ges_status") }}</template>
      </base-select>

      <base-textarea
        v-model="payload.ges_description"
        :value="payload.ges_description"
        @input="update('ges_description')"
      >
        <template #label>
          {{ t("employee.labels.ges_description") }}
        </template>
      </base-textarea>

      <div class="flex flex-row gap-1">
        <base-date
          v-model="payload.start_date"
          :value="payload.start_date"
          :required="true"
          value-mask="YYYY-MM-DD"
          placeholder="--/--/----"
          @update:model-value="update('start_date')"
        >
          <template #label> {{ t("employee.labels.start_date") }}</template>
        </base-date>

        <base-date
          v-model="payload.end_date"
          :value="payload.end_date"
          :required="true"
          value-mask="YYYY-MM-DD"
          placeholder="--/--/----"
          @update:model-value="update('end_date')"
        >
          <template #label> {{ t("employee.labels.end_date") }}</template>
        </base-date>
      </div>

      <base-textarea
        v-model="payload.description"
        :value="payload.description"
        :placeholder="t('common.placeholders.describe')"
        @input="update('description')"
      >
        <template #label>
          {{ t("common.labels.description") }}
        </template>
      </base-textarea>
    </form>
  </section>
</template>

<style lang="scss" scoped>
.form-content {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;

  .title {
    display: inline-flex;
    align-items: center;
    gap: 1rem;
    font-size: 1.5rem;
    font-weight: bold;
    text-transform: uppercase;
  }

  form {
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }

  .documents {
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }
}
</style>
